import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
  Modal,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../context/AuthContext';
import { earlyPickupAPI } from '../services/api';
import { EarlyPickupCollector, EarlyPickupRequest, EarlyPickupStatus } from '../types';
import { WebPageShell } from '../components/common/WebPageShell';
import { Toast } from '../components/common/Toast';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { Logger } from '../utils/logger';
import {
  EarlyPickupFormState,
  MAX_TEXT_LENGTH,
  REASON_OPTIONS,
  STATUS_FILTERS,
  STATUS_META,
  StatusTone,
  extractApiErrorMessage,
  formatPickupDate,
  formatPickupTime,
  formatTimestamp,
  normalizeTimeInput,
  toApiTime,
  todayDateString,
  validateEarlyPickupForm,
} from '../utils/earlyPickup';

type StatusFilter = EarlyPickupStatus | 'ALL';

// A plain string handed to JSX renders as that DOM tag. This file is web-only, and
// RN's <TextInput> has no date/time variant — a raw <input type="date"|"time"> is what
// gives the browser's own calendar and clock pickers, instead of asking a parent to
// hand-type "YYYY-MM-DD" into a free-text box and hoping they get the format right.
// Same trick CameraCaptureModal.web.tsx uses for <video>.
const WebInput: any = 'input';

/** Plain CSS (not RN StyleSheet) — a raw DOM input can't consume registered RN styles. */
const webFieldStyle = (colors: ThemeColors, hasError: boolean): Record<string, any> => ({
  border: `1px solid ${hasError ? '#DC2626' : colors.border}`,
  borderRadius: 8,
  padding: 12,
  marginBottom: 4,
  color: colors.textPrimary,
  backgroundColor: colors.card,
  fontSize: 14,
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
});

function toneColors(colors: ThemeColors, tone: StatusTone): { fg: string; bg: string } {
  switch (tone) {
    case 'pending':
      return { fg: colors.warning, bg: colors.warningLight };
    case 'approved':
      return { fg: colors.primary, bg: colors.primaryLight };
    case 'rejected':
      return { fg: colors.danger, bg: colors.dangerLight };
    case 'checkedOut':
      return { fg: colors.success, bg: colors.successLight };
    case 'cancelled':
    default:
      return { fg: colors.textSecondary, bg: colors.divider };
  }
}

const EMPTY_FORM: EarlyPickupFormState = {
  pickupDate: todayDateString(),
  pickupTime: '',
  reasonCode: '',
  reasonText: '',
  collectorContactId: '',
  notes: '',
};

export function EarlyPickupScreen() {
  const { students, selectedStudent, selectStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width: winWidth } = useWindowDimensions();
  const isMobile = winWidth < 768;

  const [requests, setRequests] = useState<EarlyPickupRequest[]>([]);
  const [statusCounts, setStatusCounts] = useState<Partial<Record<EarlyPickupStatus, number>>>({});
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [collectors, setCollectors] = useState<EarlyPickupCollector[]>([]);
  const [collectorsLoading, setCollectorsLoading] = useState(false);
  const [collectorsError, setCollectorsError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<EarlyPickupFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [cancelTarget, setCancelTarget] = useState<EarlyPickupRequest | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const loadRequests = useCallback(async () => {
    if (!selectedStudent) {
      setIsLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      setLoadError(null);
      const { data } = await earlyPickupAPI.getMyRequests({
        studentId: selectedStudent.id,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      });
      setRequests(data?.items ?? []);
      setStatusCounts(data?.statusCounts ?? {});
    } catch (err) {
      Logger.error('EarlyPickup', 'Failed to load early pickup requests', err);
      setLoadError(extractApiErrorMessage(err, 'Could not load your early pickup requests.'));
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [selectedStudent, statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const loadCollectors = useCallback(async () => {
    if (!selectedStudent) return;
    setCollectorsLoading(true);
    try {
      setCollectorsError(null);
      const { data } = await earlyPickupAPI.getCollectors(selectedStudent.id);
      setCollectors(data ?? []);
    } catch (err) {
      Logger.error('EarlyPickup', 'Failed to load collectors', err);
      setCollectors([]);
      setCollectorsError(
        extractApiErrorMessage(err, 'Could not load the people approved to collect your child.'),
      );
    } finally {
      setCollectorsLoading(false);
    }
  }, [selectedStudent]);

  // Collectors are per-child, so a sibling's contact id must never survive a child switch.
  useEffect(() => {
    setForm(f => ({ ...f, collectorContactId: '' }));
    loadCollectors();
  }, [loadCollectors]);

  const submitRequest = async () => {
    const validationErrors = validateEarlyPickupForm(form, !!selectedStudent);
    setErrors(validationErrors);
    setSubmitError(null);
    if (Object.keys(validationErrors).length > 0 || !selectedStudent || !form.reasonCode) {
      return;
    }

    const normalizedTime = normalizeTimeInput(form.pickupTime);
    if (!normalizedTime) return;

    setSubmitting(true);
    try {
      await earlyPickupAPI.createRequest({
        studentId: selectedStudent.id,
        pickupDate: form.pickupDate,
        requestedPickupTime: toApiTime(normalizedTime),
        reasonCode: form.reasonCode,
        reasonText: form.reasonText.trim() ? form.reasonText.trim() : undefined,
        collectorContactId: form.collectorContactId,
        notes: form.notes.trim() ? form.notes.trim() : undefined,
      });
      setForm({ ...EMPTY_FORM, pickupDate: todayDateString() });
      setErrors({});
      setShowForm(false);
      setToastMessage('Early pickup request sent to the school.');
      setShowToast(true);
      loadRequests();
    } catch (err) {
      Logger.error('EarlyPickup', 'Failed to submit early pickup request', err);
      setSubmitError(extractApiErrorMessage(err, 'Could not send your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await earlyPickupAPI.cancelRequest(cancelTarget.id, cancelReason);
      setCancelTarget(null);
      setCancelReason('');
      setToastMessage('Early pickup request cancelled.');
      setShowToast(true);
      loadRequests();
    } catch (err) {
      Logger.error('EarlyPickup', 'Failed to cancel early pickup request', err);
      setCancelError(extractApiErrorMessage(err, 'Could not cancel this request.'));
    } finally {
      setCancelling(false);
    }
  };

  const renderItem = ({ item }: { item: EarlyPickupRequest }) => {
    const meta = STATUS_META[item.status];
    const tone = toneColors(colors, meta.tone);
    const pickupDate = formatPickupDate(item.pickupDate);
    const reasonLabel =
      REASON_OPTIONS.find(option => option.code === item.reasonCode)?.label ?? item.reasonCode;

    return (
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderText}>
            <Text style={styles.studentName}>{item.studentName}</Text>
            <Text style={styles.cardMeta}>
              {[item.classDisplayName, item.rollNumber ? `Roll ${item.rollNumber}` : null]
                .filter(Boolean)
                .join(' • ')}
            </Text>
          </View>
          <View style={[styles.statusPill, { backgroundColor: tone.bg, borderColor: tone.fg }]}>
            <FeatherIcon name={meta.icon} size={13} color={tone.fg} />
            <Text style={[styles.statusPillText, { color: tone.fg }]}>{meta.label}</Text>
          </View>
        </View>

        <View style={[styles.statusMeaning, { backgroundColor: tone.bg }]}>
          <FeatherIcon name={meta.icon} size={14} color={tone.fg} />
          <Text style={[styles.statusMeaningText, { color: tone.fg }]}>{meta.meaning}</Text>
        </View>

        <Text style={styles.detailLine}>
          {pickupDate.date}
          {pickupDate.day ? ` · ${pickupDate.day}` : ''} · pickup at{' '}
          {formatPickupTime(item.requestedPickupTime)}
        </Text>
        <Text style={styles.detailLine}>Reason: {reasonLabel}</Text>
        {!!item.reasonText && <Text style={styles.detailSubLine}>{item.reasonText}</Text>}
        <Text style={styles.detailLine}>
          Pickup person: {item.collectorName}
          {item.collectorRelationship ? ` (${item.collectorRelationship})` : ''}
          {item.collectorContactNumber ? ` · ${item.collectorContactNumber}` : ''}
        </Text>
        {!!item.notes && <Text style={styles.detailSubLine}>Notes: {item.notes}</Text>}

        {item.status === 'APPROVED' && (
          <View style={styles.outcomeBox}>
            <Text style={styles.outcomeLabel}>Approved by the school</Text>
            <Text style={styles.outcomeText}>
              {[item.approvedByName, formatTimestamp(item.approvedAt)].filter(Boolean).join(' · ') ||
                'Approval recorded.'}
            </Text>
            <Text style={styles.outcomeText}>
              Your child has not been collected yet — please arrive at the school office.
            </Text>
          </View>
        )}

        {item.status === 'REJECTED' && (
          <View style={[styles.outcomeBox, styles.outcomeBoxRejected]}>
            <Text style={[styles.outcomeLabel, { color: colors.danger }]}>Reason for rejection</Text>
            <Text style={styles.outcomeText}>
              {item.rejectionReason ?? 'The school did not give a reason.'}
            </Text>
            {!!(item.rejectedByName || item.rejectedAt) && (
              <Text style={styles.outcomeMeta}>
                {[item.rejectedByName, formatTimestamp(item.rejectedAt)].filter(Boolean).join(' · ')}
              </Text>
            )}
          </View>
        )}

        {item.status === 'CHECKED_OUT' && (
          <View style={styles.outcomeBox}>
            <Text style={[styles.outcomeLabel, { color: colors.success }]}>Collected</Text>
            <Text style={styles.outcomeText}>
              Left the school at {formatPickupTime(item.actualCheckoutTime) || 'the recorded time'}
              {item.checkedOutByName ? `, released by ${item.checkedOutByName}` : ''}.
            </Text>
          </View>
        )}

        {item.status === 'CANCELLED' && !!(item.cancelledByName || item.cancelledAt) && (
          <View style={styles.outcomeBox}>
            <Text style={styles.outcomeLabel}>Cancelled</Text>
            <Text style={styles.outcomeText}>
              {[item.cancelledByName, formatTimestamp(item.cancelledAt)].filter(Boolean).join(' · ')}
            </Text>
          </View>
        )}

        {item.studentAlreadyAbsent && (
          <View style={styles.noticeBox}>
            <FeatherIcon name="alert-triangle" size={14} color={colors.warning} />
            <Text style={styles.noticeText}>
              School records show your child is marked absent for this day.
            </Text>
          </View>
        )}

        {!!item.lateArrivalTime && (
          <View style={styles.noticeBox}>
            <FeatherIcon name="info" size={14} color={colors.warning} />
            <Text style={styles.noticeText}>
              Your child arrived late, at {formatPickupTime(item.lateArrivalTime)}.
            </Text>
          </View>
        )}

        <View style={styles.cardFooter}>
          <Text style={styles.submittedText}>Requested {formatTimestamp(item.submittedAt)}</Text>
          {item.canCancel && (
            <TouchableOpacity
              style={styles.cancelRequestBtn}
              onPress={() => {
                setCancelTarget(item);
                setCancelReason('');
                setCancelError(null);
              }}>
              <FeatherIcon name="x" size={13} color={colors.danger} />
              <Text style={styles.cancelRequestText}>Cancel request</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const selectedCollector = collectors.find(c => c.id === form.collectorContactId);

  return (
    <WebPageShell title="Early Pickup">
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <View>
          <Text style={styles.title}>Early Pickup Requests</Text>
          <Text style={styles.subtitle}>
            {selectedStudent
              ? `Requests you raised for ${selectedStudent.name}`
              : 'No child selected'}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.addBtn, !selectedStudent && styles.addBtnDisabled]}
          onPress={() => {
            setSubmitError(null);
            setErrors({});
            setShowForm(true);
          }}
          disabled={!selectedStudent}>
          <FeatherIcon name="plus" size={18} color="#FFF" />
          <Text style={styles.addBtnText}>New request</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterRow}>
        {STATUS_FILTERS.map(filter => {
          const isActive = statusFilter === filter.value;
          const count =
            filter.value === 'ALL'
              ? Object.values(statusCounts).reduce((sum, value) => sum + (value ?? 0), 0)
              : statusCounts[filter.value] ?? 0;
          return (
            <TouchableOpacity
              key={filter.value}
              style={[styles.filterChip, isActive && styles.filterChipActive]}
              onPress={() => setStatusFilter(filter.value)}>
              <Text style={[styles.filterChipText, isActive && styles.filterChipTextActive]}>
                {filter.label} ({count})
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {!!loadError && (
        <View style={styles.errorBanner}>
          <FeatherIcon name="alert-circle" size={16} color={colors.danger} />
          <Text style={styles.errorBannerText}>{loadError}</Text>
          <TouchableOpacity onPress={loadRequests}>
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading pickup requests..." />
      ) : !selectedStudent ? (
        <View style={styles.empty}>
          <FeatherIcon name="user-x" size={40} color={colors.textMuted} />
          <Text style={styles.emptyText}>Select a child to request an early pickup</Text>
        </View>
      ) : requests.length === 0 && !loadError ? (
        <View style={styles.empty}>
          <FeatherIcon name="inbox" size={40} color={colors.textMuted} />
          <Text style={styles.emptyText}>
            {statusFilter === 'ALL'
              ? 'No early pickup requests yet'
              : `No ${STATUS_META[statusFilter as EarlyPickupStatus].label.toLowerCase()} requests`}
          </Text>
        </View>
      ) : (
        <FlatList
          data={requests}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          scrollEnabled
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true);
                loadRequests();
              }}
            />
          }
        />
      )}

      {/* "fade", not "slide" — slide animates up from the bottom edge, which on web read
          as a mobile bottom-sheet and matched nothing else in the app. The cancel modal
          below (and the app's other dialogs) already fade into the centre. */}
      <Modal visible={showForm} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Request Early Pickup</Text>
              <TouchableOpacity onPress={() => setShowForm(false)} disabled={submitting}>
                <FeatherIcon name="x" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              <Text style={styles.label}>Child</Text>
              {students.length > 1 ? (
                <View style={styles.optionWrap}>
                  {students.map(student => {
                    const isActive = student.id === selectedStudent?.id;
                    return (
                      <TouchableOpacity
                        key={student.id}
                        style={[styles.optionChip, isActive && styles.optionChipActive]}
                        onPress={() => selectStudent(student)}>
                        <Text
                          style={[styles.optionChipText, isActive && styles.optionChipTextActive]}>
                          {student.name}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ) : (
                <Text style={styles.readOnlyText}>{selectedStudent?.name}</Text>
              )}
              <Text style={styles.helperText}>
                This request is for {selectedStudent?.name}
                {selectedStudent?.class ? ` · ${selectedStudent.class}` : ''}.
              </Text>

              <Text style={styles.label}>
                Pickup date <Text style={styles.required}>*</Text>
              </Text>
              {/* type="date" gives the browser's calendar picker and guarantees the
                  YYYY-MM-DD the API expects. min stops a past date being picked at all,
                  rather than only rejecting it after Send is pressed. */}
              <WebInput
                type="date"
                style={webFieldStyle(colors, !!errors.pickupDate)}
                value={form.pickupDate}
                min={todayDateString()}
                disabled={submitting}
                onChange={(e: any) => setForm(f => ({ ...f, pickupDate: e.target.value }))}
              />
              {!!errors.pickupDate && <Text style={styles.errorText}>{errors.pickupDate}</Text>}

              <Text style={styles.label}>
                Pickup time <Text style={styles.required}>*</Text>
              </Text>
              {/* type="time" emits "HH:mm" 24-hour regardless of the locale it displays
                  in, which is exactly what normalizeTimeInput/toApiTime already expect. */}
              <WebInput
                type="time"
                style={webFieldStyle(colors, !!errors.pickupTime)}
                value={form.pickupTime}
                disabled={submitting}
                onChange={(e: any) => setForm(f => ({ ...f, pickupTime: e.target.value }))}
              />
              {!!errors.pickupTime && <Text style={styles.errorText}>{errors.pickupTime}</Text>}

              <Text style={styles.label}>
                Reason <Text style={styles.required}>*</Text>
              </Text>
              <View style={styles.optionWrap}>
                {REASON_OPTIONS.map(option => {
                  const isActive = form.reasonCode === option.code;
                  return (
                    <TouchableOpacity
                      key={option.code}
                      style={[styles.optionChip, isActive && styles.optionChipActive]}
                      onPress={() => setForm(f => ({ ...f, reasonCode: option.code }))}>
                      <Text
                        style={[styles.optionChipText, isActive && styles.optionChipTextActive]}>
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {!!errors.reasonCode && <Text style={styles.errorText}>{errors.reasonCode}</Text>}

              {form.reasonCode === 'OTHER' && (
                <>
                  <Text style={styles.label}>
                    Tell us more <Text style={styles.required}>*</Text>
                  </Text>
                  <TextInput
                    style={[styles.input, styles.textArea, !!errors.reasonText && styles.inputError]}
                    placeholder="Why does your child need to leave early?"
                    placeholderTextColor={colors.textMuted}
                    value={form.reasonText}
                    onChangeText={value => setForm(f => ({ ...f, reasonText: value }))}
                    multiline
                    maxLength={MAX_TEXT_LENGTH}
                    editable={!submitting}
                  />
                  {!!errors.reasonText && <Text style={styles.errorText}>{errors.reasonText}</Text>}
                </>
              )}

              <Text style={styles.label}>
                Pickup person <Text style={styles.required}>*</Text>
              </Text>
              {collectorsLoading ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : collectorsError ? (
                <View style={styles.errorBanner}>
                  <FeatherIcon name="alert-circle" size={16} color={colors.danger} />
                  <Text style={styles.errorBannerText}>{collectorsError}</Text>
                  <TouchableOpacity onPress={loadCollectors}>
                    <Text style={styles.retryText}>Retry</Text>
                  </TouchableOpacity>
                </View>
              ) : collectors.length === 0 ? (
                <Text style={styles.helperText}>
                  The school has no approved contacts on file for this child. Contact the school
                  office to add one before requesting a pickup.
                </Text>
              ) : (
                collectors.map(collector => {
                  const isActive = form.collectorContactId === collector.id;
                  return (
                    <TouchableOpacity
                      key={collector.id}
                      style={[styles.collectorRow, isActive && styles.collectorRowActive]}
                      onPress={() => setForm(f => ({ ...f, collectorContactId: collector.id }))}>
                      <FeatherIcon
                        name={isActive ? 'check-circle' : 'circle'}
                        size={18}
                        color={isActive ? colors.primary : colors.textMuted}
                      />
                      <View style={styles.collectorInfo}>
                        <Text style={styles.collectorName}>
                          {collector.name}
                        </Text>
                        <Text style={styles.collectorMeta}>
                          {[collector.relationship, collector.contactNumber]
                            .filter(Boolean)
                            .join(' · ') || 'Approved by the school'}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                })
              )}
              {!!errors.collectorContactId && (
                <Text style={styles.errorText}>{errors.collectorContactId}</Text>
              )}
              {!!selectedCollector && (
                <Text style={styles.helperText}>
                  The school will release your child to {selectedCollector.name} only.
                </Text>
              )}

              <Text style={styles.label}>Notes for the school (optional)</Text>
              <TextInput
                style={[styles.input, styles.textArea, !!errors.notes && styles.inputError]}
                placeholder="Anything the school office should know..."
                placeholderTextColor={colors.textMuted}
                value={form.notes}
                onChangeText={value => setForm(f => ({ ...f, notes: value }))}
                multiline
                maxLength={MAX_TEXT_LENGTH}
                editable={!submitting}
              />
              {!!errors.notes && <Text style={styles.errorText}>{errors.notes}</Text>}

              {!!submitError && (
                <View style={styles.errorBanner}>
                  <FeatherIcon name="alert-circle" size={16} color={colors.danger} />
                  <Text style={styles.errorBannerText}>{submitError}</Text>
                </View>
              )}
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setShowForm(false)}
                disabled={submitting}>
                <Text style={styles.cancelBtnText}>Close</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.submitBtn, submitting && styles.submitBtnDisabled]}
                onPress={submitRequest}
                disabled={submitting}>
                {submitting ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.submitBtnText}>Send Request</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!cancelTarget} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Cancel this request?</Text>
              <TouchableOpacity onPress={() => setCancelTarget(null)} disabled={cancelling}>
                <FeatherIcon name="x" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            {!!cancelTarget && (
              <Text style={styles.helperText}>
                {cancelTarget.studentName} · {formatPickupDate(cancelTarget.pickupDate).date} at{' '}
                {formatPickupTime(cancelTarget.requestedPickupTime)}
              </Text>
            )}
            <TextInput
              style={[styles.input, styles.textArea]}
              placeholder="Reason (optional)"
              placeholderTextColor={colors.textMuted}
              value={cancelReason}
              onChangeText={setCancelReason}
              multiline
              maxLength={MAX_TEXT_LENGTH}
              editable={!cancelling}
            />
            {!!cancelError && (
              <View style={styles.errorBanner}>
                <FeatherIcon name="alert-circle" size={16} color={colors.danger} />
                <Text style={styles.errorBannerText}>{cancelError}</Text>
              </View>
            )}
            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setCancelTarget(null)}
                disabled={cancelling}>
                <Text style={styles.cancelBtnText}>Keep request</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.dangerBtn}
                onPress={confirmCancel}
                disabled={cancelling}>
                {cancelling ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.submitBtnText}>Cancel request</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Toast
        visible={showToast}
        message={toastMessage}
        type="success"
        onDismiss={() => setShowToast(false)}
      />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 },
  headerMobile: { flexDirection: 'column', alignItems: 'stretch' },
  title: { fontSize: 22, fontWeight: '800', color: colors.textPrimary },
  subtitle: { fontSize: 13, color: colors.textSecondary, marginTop: 4 },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, height: 40, borderRadius: 20, backgroundColor: colors.primary, justifyContent: 'center' },
  addBtnDisabled: { opacity: 0.5 },
  addBtnText: { color: '#FFF', fontSize: 13, fontWeight: '700' },
  filterRow: { gap: 8, paddingBottom: 16 },
  filterChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border },
  filterChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  filterChipText: { fontSize: 12, fontWeight: '600', color: colors.textSecondary },
  filterChipTextActive: { color: '#FFF' },
  loader: { marginTop: 40 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, marginTop: 12, fontWeight: '600', textAlign: 'center' },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 16, marginBottom: 12, borderWidth: 1, borderColor: colors.divider, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 12 },
  cardHeaderText: { flex: 1 },
  studentName: { fontSize: 15, fontWeight: '800', color: colors.textPrimary },
  cardMeta: { fontSize: 12, color: colors.textMuted, marginTop: 2 },
  statusPill: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 },
  statusPillText: { fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.4 },
  statusMeaning: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, borderRadius: 10, padding: 10, marginBottom: 12 },
  statusMeaningText: { flex: 1, fontSize: 12, fontWeight: '600', lineHeight: 18 },
  detailLine: { fontSize: 13, color: colors.textPrimary, marginBottom: 4 },
  detailSubLine: { fontSize: 12, color: colors.textSecondary, marginBottom: 4, lineHeight: 18 },
  outcomeBox: { backgroundColor: colors.background, borderRadius: 10, padding: 12, marginTop: 8 },
  outcomeBoxRejected: { borderLeftWidth: 3, borderLeftColor: colors.danger },
  outcomeLabel: { fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.4, color: colors.textSecondary, marginBottom: 4 },
  outcomeText: { fontSize: 12, color: colors.textPrimary, lineHeight: 18 },
  outcomeMeta: { fontSize: 11, color: colors.textSecondary, marginTop: 4 },
  noticeBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, backgroundColor: colors.warningLight, borderRadius: 10, padding: 10, marginTop: 8 },
  noticeText: { flex: 1, fontSize: 12, color: colors.textPrimary },
  cardFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: colors.divider, gap: 12 },
  submittedText: { flex: 1, fontSize: 11, color: colors.textMuted },
  cancelRequestBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderColor: colors.danger, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  cancelRequestText: { fontSize: 12, fontWeight: '700', color: colors.danger },
  errorBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.dangerLight, borderRadius: 10, padding: 12, marginBottom: 12 },
  errorBannerText: { flex: 1, fontSize: 12, color: colors.textPrimary, lineHeight: 18 },
  retryText: { fontSize: 12, fontWeight: '700', color: colors.primary },
  modalOverlay: { flex: 1, backgroundColor: colors.overlay, justifyContent: 'center', alignItems: 'center' },
  modal: { backgroundColor: colors.card, borderRadius: 16, padding: 20, maxHeight: '90%', width: '100%', maxWidth: 560 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: '800', color: colors.textPrimary },
  label: { fontSize: 13, fontWeight: '600', color: colors.textSecondary, marginBottom: 6, marginTop: 10 },
  required: { color: '#DC2626' },
  helperText: { fontSize: 12, color: colors.textMuted, marginBottom: 6, lineHeight: 18 },
  readOnlyText: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: 12, marginBottom: 4, color: colors.textPrimary },
  textArea: { minHeight: 90, textAlignVertical: 'top' },
  inputError: { borderColor: '#DC2626' },
  errorText: { fontSize: 11, color: '#DC2626', fontWeight: '600', marginBottom: 6 },
  optionWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
  optionChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border },
  optionChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  optionChipText: { fontSize: 12, fontWeight: '600', color: colors.textSecondary },
  optionChipTextActive: { color: '#FFF' },
  collectorRow: { flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 12, marginBottom: 8 },
  collectorRowActive: { borderColor: colors.primary, backgroundColor: colors.primaryLight },
  collectorInfo: { flex: 1 },
  collectorName: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  collectorMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  modalFooter: { flexDirection: 'row', gap: 12, marginTop: 16 },
  cancelBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: colors.border, alignItems: 'center' },
  cancelBtnText: { fontSize: 14, fontWeight: '700', color: colors.textSecondary },
  submitBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: colors.primary, alignItems: 'center' },
  submitBtnDisabled: { opacity: 0.6 },
  submitBtnText: { fontSize: 14, fontWeight: '700', color: '#FFF' },
  dangerBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: colors.danger, alignItems: 'center' },
});
